/**
 * React hook for managing a real-time voice conversation session.
 *
 * Connects to the backend WebSocket relay which forwards audio to/from
 * the OpenAI Realtime API. Handles audio capture, playback, state
 * transitions, transcripts, and interruption.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AudioCapture } from "@/lib/audio/capture";
import { AudioPlayback } from "@/lib/audio/playback";
import { getBaseURL } from "@/lib/api-base-url";
import { getAccessToken } from "@/lib/tokenStorage";
import { log } from '@/lib/logger';

// ---------------------------------------------------------------------------
// Silence detection constants
// ---------------------------------------------------------------------------

/** Average frequency level (0-255) below which we consider silence. */
const SILENCE_THRESHOLD = 10;
/** Level above which we consider speech has started. */
const SPEECH_THRESHOLD = 20;
/** How long silence must persist (ms) after speech before we auto-commit. */
const SILENCE_COMMIT_MS = 2000;
/** How often we poll the analyser for silence detection (ms). */
const SILENCE_POLL_MS = 150;
/** Max time (ms) in "listening" with no speech before we consider it idle. */
const LISTENING_IDLE_TIMEOUT_MS = 30_000;
/**
 * Echo gate: ms to wait after entering "listening" before sending mic audio.
 * Prevents speaker output from feeding back into OpenAI's server VAD.
 */
const ECHO_COOLDOWN_MS = 600;

// ---------------------------------------------------------------------------
// Debug logging — filter browser console with "[voice]"
// ---------------------------------------------------------------------------

const VOICE_DEBUG = import.meta.env.DEV;

/** Throttled logger: only logs a given tag once per interval. */
const _lastLog: Record<string, number> = {};
function vlog(tag: string, ...args: unknown[]) {
  if (!VOICE_DEBUG) return;
  const now = Date.now();
  // Throttle repeated tags to once per 500ms (e.g. mic-gated)
  if (_lastLog[tag] && now - _lastLog[tag] < 500) return;
  _lastLog[tag] = now;
  log.debug('VOICE', `[${tag}]`, ...args);
}

/** Non-throttled — use for infrequent events (state changes, commits). */
function vlogOnce(tag: string, ...args: unknown[]) {
  if (!VOICE_DEBUG) return;
  log.debug('VOICE', `[${tag}]`, ...args);
}

/** Diagnostic log — always include [voice][diag] for grep; controlled by VOICE_DEBUG. */
function vlogDiag(step: string, detail?: Record<string, unknown>) {
  if (!VOICE_DEBUG) return;
  log.debug('VOICE', `[diag] ${step}`, detail ?? "");
}

/** Set VITE_VOICE_OFFLINE=true in .env.local to test voice UI without backend (no WebSocket, goes to "listening" only). */
const VOICE_OFFLINE = typeof import.meta !== "undefined" && import.meta.env?.VITE_VOICE_OFFLINE === "true";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type VoiceState =
  | "idle"
  | "connecting"
  | "listening"
  | "thinking"
  | "speaking"
  | "error";

export interface VoiceTranscript {
  role: "user" | "assistant";
  text: string;
  final: boolean;
  ts: number;
}

export interface UseVoiceSessionReturn {
  state: VoiceState;
  isActive: boolean;
  transcripts: VoiceTranscript[];
  error: string | null;
  startSession: () => Promise<void>;
  endSession: () => void;
  inputAnalyser: AnalyserNode | null;
  outputAnalyser: AnalyserNode | null;
  elapsed: number;
  /** True when VITE_VOICE_OFFLINE=true (demo mode, no backend). */
  isOffline: boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useVoiceSession(): UseVoiceSessionReturn {
  const [state, setState] = useState<VoiceState>("idle");
  const [transcripts, setTranscripts] = useState<VoiceTranscript[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [inputAnalyser, setInputAnalyser] = useState<AnalyserNode | null>(null);
  const [outputAnalyser, setOutputAnalyser] = useState<AnalyserNode | null>(null);
  const [elapsed, setElapsed] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const captureRef = useRef<AudioCapture | null>(null);
  const playbackRef = useRef<AudioPlayback | null>(null);
  const startTimeRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cleaningUpRef = useRef(false);

  // Silence detection refs
  const silenceTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasSpeechRef = useRef(false);
  const silenceStartRef = useRef<number | null>(null);
  const committedRef = useRef(false);
  const stateRef = useRef<VoiceState>("idle");
  /** Timestamp when we last entered "listening" state. */
  const listeningStartRef = useRef<number | null>(null);
  /** Timestamp when "listening" state was entered — used for echo cooldown. */
  const micUnmutedAtRef = useRef(0);

  // Keep stateRef in sync for use inside intervals/callbacks
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // ---- Helpers ----

  const buildWsUrl = useCallback(async (): Promise<string> => {
    const base = getBaseURL(); // e.g. http://localhost:8000
    const wsBase = base.replace(/^http/, "ws");
    const token = await getAccessToken();
    // Mobile: pass token as query param; Web: session cookie is sent automatically
    if (token) {
      return `${wsBase}/api/voice/session?token=${encodeURIComponent(token)}`;
    }
    return `${wsBase}/api/voice/session`;
  }, []);

  // ---- Silence detection ----

  const stopSilenceDetection = useCallback(() => {
    if (silenceTimerRef.current) {
      clearInterval(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    hasSpeechRef.current = false;
    silenceStartRef.current = null;
    committedRef.current = false;
    listeningStartRef.current = null;
  }, []);

  const startSilenceDetection = useCallback(() => {
    stopSilenceDetection();
    listeningStartRef.current = Date.now();

    silenceTimerRef.current = setInterval(() => {
      // Only run during "listening" state
      if (stateRef.current !== "listening") {
        // Reset tracking when not listening (e.g. thinking/speaking)
        hasSpeechRef.current = false;
        silenceStartRef.current = null;
        committedRef.current = false;
        listeningStartRef.current = null;
        return;
      }

      // Start tracking listening time if not yet set (after a state reset)
      if (listeningStartRef.current === null) {
        listeningStartRef.current = Date.now();
      }

      if (committedRef.current) return;

      const analyser = captureRef.current?.getAnalyser();
      if (!analyser) return;

      const data = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(data);

      // Compute average level
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      const avg = sum / data.length;

      if (avg >= SPEECH_THRESHOLD) {
        // User is speaking
        if (!hasSpeechRef.current) {
          vlogOnce("silence-det", `speech detected (avg=${avg.toFixed(1)})`);
        }
        hasSpeechRef.current = true;
        silenceStartRef.current = null;
        // Reset idle clock — user is actively speaking
        listeningStartRef.current = Date.now();
      } else if (avg < SILENCE_THRESHOLD && hasSpeechRef.current) {
        // Silence after speech
        if (silenceStartRef.current === null) {
          silenceStartRef.current = Date.now();
          vlogOnce("silence-det", "silence started after speech");
        } else if (Date.now() - silenceStartRef.current >= SILENCE_COMMIT_MS) {
          // Silence persisted long enough — commit audio as fallback
          committedRef.current = true;
          vlogOnce("silence-det", `client commit_audio (silence ${SILENCE_COMMIT_MS}ms)`);
          const ws = wsRef.current;
          if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "commit_audio" }));
          }
        }
      } else {
        vlog("silence-det", `ambient avg=${avg.toFixed(1)} hasSpeech=${hasSpeechRef.current}`);
      }

      // Idle timeout: if listening for too long with no speech, reset the
      // timer so we don't commit an empty buffer (OpenAI rejects 0ms audio).
      if (
        !hasSpeechRef.current &&
        listeningStartRef.current &&
        Date.now() - listeningStartRef.current >= LISTENING_IDLE_TIMEOUT_MS
      ) {
        vlogOnce("silence-det", `idle timeout (${LISTENING_IDLE_TIMEOUT_MS}ms) — no speech, resetting`);
        listeningStartRef.current = Date.now();
      }
    }, SILENCE_POLL_MS);
  }, [stopSilenceDetection]);

  // ---- Cleanup ----

  const cleanup = useCallback(async () => {
    if (cleaningUpRef.current) return;
    cleaningUpRef.current = true;
    vlogDiag("cleanup_begin");

    stopSilenceDetection();

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "end_session" }));
        ws.close(1000);
      }
    }

    if (captureRef.current) {
      await captureRef.current.stop();
      captureRef.current = null;
    }

    if (playbackRef.current) {
      await playbackRef.current.destroy();
      playbackRef.current = null;
    }

    setInputAnalyser(null);
    setOutputAnalyser(null);
    setElapsed(0);
    setState("idle");
    cleaningUpRef.current = false;
    vlogDiag("cleanup_done");
  }, [stopSilenceDetection]);

  // ---- Handle server JSON messages ----

  const handleServerMessage = useCallback((msg: Record<string, unknown>) => {
    const type = msg.type as string;

    if (type === "session_connecting") {
      vlogOnce("server", "session_connecting — server is setting up");
    } else if (type === "interrupt") {
      vlogOnce("server", "interrupt — stopping playback");
      playbackRef.current?.stop();
    } else if (type === "state") {
      const s = msg.state as string;
      const pbPlaying = playbackRef.current?.isPlaying() ?? false;
      vlogOnce("state", `${stateRef.current} → ${s}`, { playbackActive: pbPlaying });
      if (s === "listening") {
        hasSpeechRef.current = false;
        silenceStartRef.current = null;
        committedRef.current = false;
        listeningStartRef.current = Date.now();
        micUnmutedAtRef.current = Date.now();
        vlogOnce("echo-gate", `cooldown started (${ECHO_COOLDOWN_MS}ms)`);
        setState("listening");
      } else if (s === "thinking") {
        committedRef.current = true;
        setState("thinking");
      } else if (s === "speaking") {
        setState("speaking");
      }
    } else if (type === "transcript") {
      const role = msg.role as "user" | "assistant";
      const text = msg.text as string;
      const final_ = msg.final as boolean;

      setTranscripts((prev) => {
        if (role === "assistant" && !final_) {
          const last = prev[prev.length - 1];
          if (last && last.role === "assistant" && !last.final) {
            return [
              ...prev.slice(0, -1),
              { ...last, text: last.text + text },
            ];
          }
          return [...prev, { role, text, final: false, ts: Date.now() }];
        }
        if (final_) {
          const last = prev[prev.length - 1];
          if (last && last.role === role && !last.final) {
            return [...prev.slice(0, -1), { role, text, final: true, ts: Date.now() }];
          }
          return [...prev, { role, text, final: true, ts: Date.now() }];
        }
        return [...prev, { role, text, final: false, ts: Date.now() }];
      });
    } else if (type === "session_ended") {
      void cleanup();
    } else if (type === "error") {
      setError(msg.message as string);
    }
  }, [cleanup]);

  // ---- Start session ----

  const startSession = useCallback(async () => {
    if (state !== "idle" && state !== "error") return;

    setError(null);
    setTranscripts([]);
    setState("connecting");
    cleaningUpRef.current = false;
    vlogDiag("startSession_begin", { offline: VOICE_OFFLINE });

    try {
      if (VOICE_OFFLINE) {
        vlogDiag("offline_mode", { message: "Using offline/demo voice session (no WebSocket)" });
        await new Promise<void>((r) => setTimeout(r, 500));
        if (cleaningUpRef.current) return;
        setInputAnalyser(null);
        setOutputAnalyser(null);
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
          setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 1000);
        setState("listening");
        vlogDiag("offline_listening");
        return;
      }

      const url = await buildWsUrl();
      vlogDiag("buildWsUrl_done", { url: url.replace(/token=[^&]+/, "token=***") });

      // Initialise audio capture & playback
      const capture = new AudioCapture();
      const playback = new AudioPlayback();
      captureRef.current = capture;
      playbackRef.current = playback;

      // Open WebSocket
      vlogDiag("ws_new", { url: url.replace(/token=[^&]+/, "token=***") });
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.binaryType = "arraybuffer";

      // ---- Set ALL WS handlers BEFORE anything async ----
      // This prevents a race where the backend closes the WS while
      // capture.start() is still running (getUserMedia + worklet load).
      ws.onmessage = (ev: MessageEvent) => {
        if (ev.data instanceof ArrayBuffer) {
          vlog("ws-in", `binary ${ev.data.byteLength}B (audio)`);
          playbackRef.current?.enqueue(ev.data);
        } else {
          try {
            const msg = JSON.parse(ev.data as string);
            vlogOnce("ws-in", `json: ${msg.type}`, msg.type === "state" ? msg.state : "");
            handleServerMessage(msg);
          } catch {
            // Ignore malformed JSON
          }
        }
      };

      ws.onclose = (ev) => {
        vlogOnce("ws-close", `code=${ev.code} reason=${ev.reason || "(none)"}`);
        vlogDiag("ws_onclose", { code: ev.code, reason: ev.reason || null, cleaningUp: cleaningUpRef.current });
        if (!cleaningUpRef.current) {
          const normalClose = ev.code === 1000;
          const noStatusReceived = ev.code === 1005 || ev.code === 1006;
          if (normalClose || noStatusReceived) {
            setError(null);
            setState("idle");
            if (noStatusReceived) {
              toast.info("Voice session ended. Try again or check your microphone.");
            }
          } else {
            setError(ev.reason || "Connection closed unexpectedly");
            setState("error");
          }
          void cleanup();
        }
      };

      ws.onerror = () => {
        vlogOnce("ws-error", "WebSocket error event");
        if (!cleaningUpRef.current) {
          setError("Connection error");
          setState("error");
        }
      };

      await new Promise<void>((resolve, reject) => {
        ws.onopen = () => {
          vlogOnce("ws-open", "WebSocket connected");
          vlogDiag("ws_onopen");
          resolve();
        };
        // Connection-phase error: also reject the promise
        const prevOnError = ws.onerror;
        ws.onerror = (e) => {
          prevOnError?.call(ws, e as Event);
          reject(new Error("WebSocket connection failed"));
        };
        setTimeout(() => reject(new Error("Connection timeout")), 10_000);
      });

      // Restore normal onerror (the reject-on-connect wrapper is no longer needed)
      ws.onerror = () => {
        vlogOnce("ws-error", "WebSocket error event");
        if (!cleaningUpRef.current) {
          setError("Connection error");
          setState("error");
        }
      };

      vlogDiag("capture_start_begin");
      // Start mic capture — send PCM16 chunks over WebSocket.
      // Gated by three conditions to prevent echo feedback loop:
      //  1. Only while in "listening" state
      //  2. Not while assistant audio is still playing through speakers
      //  3. Not until ECHO_COOLDOWN_MS after entering "listening" (speaker dissipation)
      await capture.start((pcm16: ArrayBuffer) => {
        // ① Chunk arrived from AudioWorklet
        vlog("mic-chunk", `${pcm16.byteLength}B ws=${ws.readyState} state=${stateRef.current}`);

        if (ws.readyState !== WebSocket.OPEN) {
          vlog("mic-gate", "blocked: ws not open", ws.readyState);
          return;
        }
        if (stateRef.current !== "listening") {
          vlog("mic-gate", "blocked: state =", stateRef.current);
          return;
        }
        if (playbackRef.current?.isPlaying()) {
          vlog("mic-gate", "blocked: playback still active");
          return;
        }
        const cooldownRemaining = ECHO_COOLDOWN_MS - (Date.now() - micUnmutedAtRef.current);
        if (cooldownRemaining > 0) {
          vlog("mic-gate", `blocked: echo cooldown ${cooldownRemaining}ms remaining`);
          return;
        }
        vlog("mic-send", `sending ${pcm16.byteLength}B`);
        ws.send(pcm16);
      });
      vlogDiag("capture_start_done");

      setInputAnalyser(capture.getAnalyser());
      setOutputAnalyser(playback.getAnalyser());

      // Elapsed timer
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);

      micUnmutedAtRef.current = Date.now();
      setState("listening");

      // Start client-side silence detection as a fallback for server VAD
      startSilenceDetection();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to start voice session";
      vlogDiag("startSession_error", { message: msg });
      setError(msg);
      setState("error");
      await cleanup();
    }
  }, [state, buildWsUrl, cleanup, startSilenceDetection, handleServerMessage]);

  // ---- End session ----

  const endSession = useCallback(() => {
    void cleanup();
  }, [cleanup]);

  // ---- Cleanup on unmount ----

  useEffect(() => {
    return () => {
      void cleanup();
    };
  }, [cleanup]);

  return {
    state,
    isActive: state !== "idle" && state !== "error",
    transcripts,
    error,
    startSession,
    endSession,
    inputAnalyser,
    outputAnalyser,
    elapsed,
    isOffline: VOICE_OFFLINE,
  };
}
